import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { v4 as uuid } from 'uuid';
import { CHECKOUT_CONFIG } from '../../config/checkout.config';
import {
  LemurApplicationResponse,
  LemurApplicationWebService,
  LemurMoneyRange,
} from '../../services/lemur-application.web.service';

const DATE_OF_BIRTH_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const GOVERNMENT_ID_PATTERN = /^\d{3}-?\d{2}-?\d{4}$/;

const USD_FORMATTER = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

@Component({
  selector: 'ws-lemur-financing-form',
  templateUrl: './lemur-financing-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LemurFinancingFormComponent implements OnInit, OnDestroy {
  @Input() sessionId = '';
  @Input() hash?: string | null;
  @Input() staffView = false;
  @Output() returnToPayNow = new EventEmitter<void>();
  @Output() financingCompleted = new EventEmitter<string>();

  readonly checkoutConfig = CHECKOUT_CONFIG;
  readonly housingStatuses = CHECKOUT_CONFIG.LEMUR_HOUSING_STATUSES;
  readonly employmentStatuses = CHECKOUT_CONFIG.LEMUR_EMPLOYMENT_STATUSES;

  submitting = false;
  refreshing = false;
  loadingExisting = true;
  submitted: LemurApplicationResponse | null = null;
  errorKind: 'validation' | 'rate_limit' | 'provider' | null = null;
  disclosureVersion: string | null = null;
  disclosureUrl: string | null = null;
  abandoned = false;
  private attemptReference?: string;
  private refreshTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly refreshDelayMs = 5_000;

  readonly form = new FormGroup({
    dateOfBirth: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(DATE_OF_BIRTH_PATTERN)],
    }),
    governmentIdNumber: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(GOVERNMENT_ID_PATTERN)],
    }),
    annualIncome: new FormControl<number | null>(null, {
      validators: [Validators.required, Validators.min(0)],
    }),
    housingStatus: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    employmentStatus: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    accepted: new FormControl(false, { nonNullable: true, validators: [Validators.requiredTrue] }),
  });

  constructor(
    private readonly lemurApplicationSvc: LemurApplicationWebService,
    private readonly toastSvc: ToastService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    void this.initialize();
  }

  ngOnDestroy(): void {
    this.stopRefreshing();
    this.clearFinancingFields();
  }

  async submit(): Promise<void> {
    this.form.markAllAsTouched();
    if (this.form.invalid || !this.sessionId || !this.disclosureVersion || this.submitting) {
      this.cdr.markForCheck();
      return;
    }

    const value = this.form.getRawValue();
    this.attemptReference ??= uuid();
    this.submitting = true;
    this.errorKind = null;
    this.abandoned = false;
    this.cdr.markForCheck();

    try {
      const result = await this.lemurApplicationSvc.submitFromSession(
        {
          sessionId: this.sessionId,
          attemptReference: this.attemptReference,
          hash: this.hash || undefined,
          applicant: {
            dateOfBirth: value.dateOfBirth,
            governmentIdNumber: value.governmentIdNumber,
            annualIncome: Number(value.annualIncome),
            housingStatus: value.housingStatus,
            employmentStatus: value.employmentStatus,
          },
          consent: {
            accepted: true,
            disclosureVersion: this.disclosureVersion,
          },
        },
        this.staffView
      );
      this.submitted = result;
      this.emitCompletion(result);
      // Any later form submission is a deliberate new application operation.
      this.attemptReference = undefined;
      this.continueRefreshingIfNeeded();
    } catch (error) {
      this.errorKind = this.errorKindFor(error);
      // A browser/network error may hide a successful provider submission. Recover by session
      // before offering a deliberate new application.
      await this.refreshStatus(true);
      this.toastSvc.show({
        type: 'error',
        title: this.errorKind === 'rate_limit' ? 'Financing is temporarily busy' : 'Financing application failed',
        text: this.errorMessage,
      });
    } finally {
      this.clearFinancingFields();
      this.submitting = false;
      this.cdr.markForCheck();
    }
  }

  async refreshStatus(silent = false): Promise<void> {
    if (!this.sessionId || this.refreshing) {
      this.loadingExisting = false;
      return;
    }
    this.refreshing = true;
    this.cdr.markForCheck();
    try {
      this.submitted = await this.lemurApplicationSvc.getStatusFromSession(this.sessionId, this.hash, this.staffView);
      this.emitCompletion(this.submitted);
      if (!this.submitted.applicationId) this.attemptReference = this.submitted.attemptReference;
      if (this.submitted.status === 'provider_error' && !this.errorKind) {
        this.errorKind = 'provider';
      }
      this.continueRefreshingIfNeeded();
    } catch (error) {
      if (!(error instanceof HttpErrorResponse && error.status === 404) && !silent) {
        this.errorKind = this.errorKindFor(error);
      }
    } finally {
      this.refreshing = false;
      this.loadingExisting = false;
      this.cdr.markForCheck();
    }
  }

  beginNewApplication(): void {
    this.stopRefreshing();
    this.submitted = null;
    this.errorKind = null;
    this.attemptReference = undefined;
    this.abandoned = false;
    this.cdr.markForCheck();
  }

  retrySameApplication(): void {
    this.stopRefreshing();
    this.submitted = null;
    this.errorKind = null;
    this.abandoned = false;
    this.cdr.markForCheck();
  }

  get canRetrySameApplication(): boolean {
    return this.submitted?.status === 'provider_error' && !this.submitted.applicationId;
  }

  choosePayNow(): void {
    if (!this.canReturnToPayNow) return;
    this.abandoned = Boolean(this.submitted && !this.isSuccessful);
    this.stopRefreshing();
    this.clearFinancingFields();
    this.returnToPayNow.emit();
  }

  get isProcessing(): boolean {
    return this.submitted?.status === 'processing' || this.submitted?.status === 'finalizing';
  }

  get isReady(): boolean {
    return this.submitted?.status === 'ready';
  }

  get isSuccessful(): boolean {
    return this.submitted?.status === 'completed';
  }

  get canReturnToPayNow(): boolean {
    if (!this.submitted) return true;
    return (
      !this.submitted.completedLoan && !['finalizing', 'completion_error', 'completed'].includes(this.submitted.status)
    );
  }

  statusLabel(value: string): string {
    return value.replace(/_/g, ' ');
  }

  moneyRange(range: LemurMoneyRange): string {
    const minimum = range.minimum_cents === null ? null : USD_FORMATTER.format(range.minimum_cents / 100);
    const maximum = range.maximum_cents === null ? null : USD_FORMATTER.format(range.maximum_cents / 100);
    if (minimum && maximum) return minimum === maximum ? minimum : `${minimum} – ${maximum}`;
    if (minimum) return `From ${minimum}`;
    if (maximum) return `Up to ${maximum}`;
    return 'Not provided';
  }

  percentRange(range: { minimum_percent: string | null; maximum_percent: string | null }): string {
    const minimum = range.minimum_percent;
    const maximum = range.maximum_percent;
    if (minimum && maximum) return minimum === maximum ? `${minimum}%` : `${minimum}% – ${maximum}%`;
    if (minimum) return `From ${minimum}%`;
    if (maximum) return `Up to ${maximum}%`;
    return 'Not provided';
  }

  monthRange(range: { minimum_months: number | null; maximum_months: number | null }): string {
    const { minimum_months: minimum, maximum_months: maximum } = range;
    if (minimum !== null && maximum !== null)
      return minimum === maximum ? `${minimum} months` : `${minimum}–${maximum} months`;
    if (minimum !== null) return `From ${minimum} months`;
    if (maximum !== null) return `Up to ${maximum} months`;
    return 'Not provided';
  }

  get applicationReference(): string | null {
    return this.submitted?.applicationId ?? this.submitted?.attemptId ?? null;
  }

  get canStartNewApplication(): boolean {
    return Boolean(
      this.submitted &&
      ['declined', 'expired', 'cancelled', 'abandoned', 'error', 'polling_exhausted', 'conflict'].includes(
        this.submitted.status
      )
    );
  }

  get errorMessage(): string {
    if (this.errorKind === 'rate_limit') return 'Please wait a moment, then start a new application or pay now.';
    if (this.errorKind === 'validation') return 'Please review the required information before trying again.';
    return 'The financing provider could not complete this request. You can refresh the status or pay now.';
  }

  private continueRefreshingIfNeeded(): void {
    this.stopRefreshing();
    if (!this.isProcessing) return;
    this.refreshTimeout = setTimeout(() => void this.refreshStatus(), this.refreshDelayMs);
  }

  private emitCompletion(application: LemurApplicationResponse): void {
    if (application.status === 'completed' && application.warrantyId) {
      this.financingCompleted.emit(application.warrantyId);
    }
  }

  private async initialize(): Promise<void> {
    try {
      const config = await this.lemurApplicationSvc.getConfigStatus();
      this.disclosureVersion = config.disclosureVersion;
      this.disclosureUrl = config.disclosureUrl;
      if (!config.usable || !this.disclosureVersion || !this.disclosureUrl) this.errorKind = 'provider';
    } catch {
      this.errorKind = 'provider';
    }
    await this.refreshStatus(true);
  }

  private stopRefreshing(): void {
    if (this.refreshTimeout) clearTimeout(this.refreshTimeout);
    this.refreshTimeout = null;
  }

  private errorKindFor(error: unknown): 'validation' | 'rate_limit' | 'provider' {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 400 || error.status === 422) return 'validation';
      if (error.status === 429) return 'rate_limit';
    }
    return 'provider';
  }

  private clearFinancingFields(): void {
    this.form.reset({
      dateOfBirth: '',
      governmentIdNumber: '',
      annualIncome: null,
      housingStatus: '',
      employmentStatus: '',
      accepted: false,
    });
  }
}
